import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { TrendingUp, Users, CreditCard, Timer, DollarSign, Gift, Loader2 } from "lucide-react";

const Stat = ({ icon: Icon, label, value, sublabel, testId, accent = false }) => (
  <div className={`vq-card p-5 ${accent ? "border-l-4 border-l-[#0055FF]" : ""}`} data-testid={testId}>
    <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">
      <Icon className="w-3.5 h-3.5" />
      {label}
    </div>
    <div className="font-display font-extrabold text-3xl text-slate-900 leading-none">{value}</div>
    {sublabel && <div className="text-xs text-slate-500 mt-2">{sublabel}</div>}
  </div>
);

export default function AdminFunnelDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/analytics/funnel");
        setData(data);
      } catch (e) {
        // silent; admin panel will just show the empty state
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="vq-card p-6 flex items-center gap-2 text-slate-500 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading funnel…
      </div>
    );
  }
  if (!data) return null;

  const plans = data.users_by_plan || {};
  const totalPromoUses = (data.promo_usage || []).reduce((s, p) => s + (p.uses || 0), 0);

  return (
    <div className="space-y-4" data-testid="admin-funnel-dashboard">
      <div className="flex items-center gap-2">
        <TrendingUp className="w-4 h-4 text-slate-500" />
        <h3 className="font-display font-bold text-lg text-slate-900">Conversion & revenue</h3>
      </div>
      <p className="text-sm text-slate-600 -mt-2">
        App-native metrics (visitor traffic + sessions live in PostHog at ap.emergent.sh).
      </p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat
          icon={Users} label="Total users"
          value={data.total_users}
          sublabel={`${data.signups_7d} in last 7d · ${data.signups_30d} in 30d`}
          testId="funnel-total-users"
        />
        <Stat
          icon={CreditCard} label="Paid subs"
          value={data.paid_subscriptions}
          sublabel={`Pro: ${plans.pro || 0} · Ent: ${plans.enterprise || 0}`}
          testId="funnel-paid-subs"
        />
        <Stat
          icon={Timer} label="Active trials"
          value={data.active_trials}
          sublabel="Trial in progress (last 30d)"
          testId="funnel-active-trials"
        />
        <Stat
          icon={DollarSign} label="Est. MRR"
          value={`$${data.estimated_mrr_usd.toLocaleString()}`}
          sublabel="Assumes paid subs stay active"
          testId="funnel-mrr"
          accent
        />
      </div>

      <div className="vq-card p-5" data-testid="funnel-promo-usage">
        <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-slate-500 mb-3">
          <Gift className="w-3.5 h-3.5" /> Promo usage
        </div>
        {(data.promo_usage || []).length === 0 ? (
          <div className="text-sm text-slate-500">No promo codes yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[10px] font-mono uppercase tracking-wider text-slate-500 border-b border-slate-100">
              <tr>
                <th className="text-left py-1.5 pr-3">Code</th>
                <th className="text-left py-1.5 pr-3">Trial</th>
                <th className="text-left py-1.5 pr-3">Uses</th>
                <th className="text-left py-1.5">Active</th>
              </tr>
            </thead>
            <tbody>
              {data.promo_usage.map((p) => (
                <tr key={p.code} className="border-b border-slate-100 last:border-b-0">
                  <td className="py-2 pr-3 font-mono font-semibold text-slate-900">{p.code}</td>
                  <td className="py-2 pr-3 text-slate-700">{p.trial_days}d</td>
                  <td className="py-2 pr-3 text-slate-700 font-mono">{p.uses}</td>
                  <td className="py-2 text-slate-500">
                    {p.active ? <span className="text-emerald-600">●</span> : <span className="text-slate-400">●</span>}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-slate-200 font-semibold">
                <td colSpan={2} className="py-2 pr-3 text-slate-700 text-xs uppercase font-mono">Total redemptions</td>
                <td colSpan={2} className="py-2 pr-3 text-slate-900 font-mono">{totalPromoUses}</td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </div>
  );
}
